<script setup lang="ts">
import { computed } from 'vue';
import { useAppearanceStore } from '@/stores/appearance';
import { normalizeHexColor, type ColorSwatch } from '@yana/shared';
import { useColorPalette } from '@/composables/useColorPalette';
import { Button } from '@/components/ui/button';

const props = withDefaults(defineProps<{
  colors?: readonly ColorSwatch[];
  value?: string | null;
  allowCustom?: boolean;
  clearLabel?: string;
  labelPrefix?: string;
  round?: boolean;
  disabled?: boolean;
}>(), {
  allowCustom: true,
  labelPrefix: 'Цвет',
});

const emit = defineEmits<{ pick: [hex: string | null] }>();
const appearance = useAppearanceStore();
const { colors: swatches, rememberColor } = useColorPalette(() => props.colors ?? appearance.fillColors, () => props.value);
const selected = computed(() => normalizeHexColor(props.value));

function pick(value: string | null): void {
  if (props.disabled) return;
  if (value === null) {
    emit('pick', null);
    return;
  }
  const hex = normalizeHexColor(value);
  if (!hex) return;
  rememberColor(hex);
  emit('pick', hex);
}
</script>

<template>
  <div class="min-w-0">
    <div class="flex max-h-40 flex-wrap items-center gap-1 overflow-y-auto p-1" data-color-palette>
      <Button
        v-for="swatch in swatches"
        :key="swatch.hex"
        type="button"
        variant="outline"
        size="icon-xs"
        class="size-6 border-black/15 p-0 transition-transform not-disabled:hover:scale-110 dark:border-white/25"
        :class="[round ? 'rounded-full' : 'rounded-sm', selected === swatch.hex ? 'ring-2 ring-primary' : '']"
        :style="{ background: swatch.hex }"
        :disabled="disabled"
        :aria-label="`${labelPrefix}: ${swatch.label}`"
        :aria-pressed="selected === swatch.hex"
        :data-tip="swatch.label"
        :data-color-swatch="swatch.hex"
        @click="pick(swatch.hex)"
      />
      <!-- Системная палитра — разрешённое исключение: в shadcn-vue нет
           аналога. change фиксирует выбор, input дал бы промежуточные цвета. -->
      <input
        v-if="allowCustom"
        type="color"
        class="size-6 shrink-0 cursor-pointer border border-border bg-card p-0.5 outline-none hover:border-ring focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        :class="round ? 'rounded-full' : 'rounded-sm'"
        :value="selected ?? '#ffffff'"
        :disabled="disabled"
        aria-label="Свой цвет"
        @change="pick(($event.target as HTMLInputElement).value)"
      />
    </div>
    <Button
      v-if="clearLabel"
      type="button"
      variant="ghost"
      size="sm"
      class="h-7 w-full justify-start px-1 text-[12px]"
      :disabled="disabled"
      @click="pick(null)"
    >
      {{ clearLabel }}
    </Button>
  </div>
</template>
